import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance. `withCredentials: true` makes the browser send the
// httpOnly access_token cookie on every request and accept Set-Cookie back.
// No more localStorage tokens — the cookie can't be read by JavaScript, which
// defeats XSS-based token theft.
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Handle auth errors — on 401, clear in-memory user state by sending the user
// to /login. There's nothing to clear in localStorage anymore.
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Don't redirect if we're already on a public/auth page — avoids loops
            const path = window.location.pathname;
            const onAuthPage = path === '/login' || path === '/register' || path === '/' || path === '/forgot-password';
            if (!onAuthPage) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword: (payload) => api.post('/auth/reset-password', payload),
};

// Documents API
export const documentsAPI = {
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/documents/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getAll: () => api.get('/documents'),
    delete: (docId) => api.delete(`/documents/${docId}`),
};

// Analysis API
export const analysisAPI = {
    analyze: () => api.post('/analysis/analyze'),
    reanalyze: () => api.post('/analysis/reanalyze'),
    getResults: () => api.get('/analysis/results'),
};

// Letters API
export const lettersAPI = {
    getPreview: () => api.get('/letters/preview'),
    getFull: () => api.get('/letters/full'),
    downloadPdfUrl: (letterId) => `${API_BASE}/letters/${letterId}/pdf`,
    downloadPdf: (letterId) => api.get(`/letters/${letterId}/pdf`, { responseType: 'blob' }),
};

// Disputes API
export const disputesAPI = {
    getAll: () => api.get('/disputes'),
    initialize: () => api.post('/disputes/initialize'),
    update: (disputeId, data) => api.put(`/disputes/${disputeId}`, data),
    getHistory: (disputeId) => api.get(`/disputes/${disputeId}/history`),
    generateCfpbComplaint: (disputeId) => api.post(`/disputes/${disputeId}/cfpb-complaint`),
};

// Payments API
export const paymentsAPI = {
    createCheckout: (originUrl) => api.post('/payments/create-checkout', { origin_url: originUrl }),
    createSubscription: (originUrl) => api.post('/payments/create-subscription', { origin_url: originUrl }),
    cancelSubscription: () => api.post('/payments/cancel-subscription'),
    getSubscriptionStatus: () => api.get('/payments/subscription-status'),
    getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
    getPricing: () => api.get('/pricing'),
};

// NMI Payments API (Collect.js frontend + Direct Post backend)
export const nmiAPI = {
    getConfig: () => api.get('/nmi/config'),
    charge: (paymentToken, billing = {}) => api.post('/nmi/charge', { payment_token: paymentToken, ...billing }),
    subscribe: (paymentToken, billing = {}) => api.post('/nmi/subscribe', { payment_token: paymentToken, ...billing }),
    createPlan: () => api.post('/admin/nmi/create-plan'),
    testCharge: (paymentToken, billing = {}) => api.post('/admin/nmi/test-charge', { payment_token: paymentToken, ...billing }),
};

// Dashboard API
export const dashboardAPI = {
    summary: () => api.get('/dashboard/summary'),
    progress: () => api.get('/dashboard/progress'),
    milestoneCard: () => api.get('/dashboard/milestone-card', { responseType: 'blob' }),
};

// Notifications API
export const notificationsAPI = {
    setPreference: (enabled) => api.post('/notifications/preferences', { enabled }),
    updatePhone: (phone) => api.put('/notifications/phone', { phone }),
    testSMS: (message) => api.post('/notifications/test-sms', { message }),
};

// Admin API
export const adminAPI = {
    getSettings: () => api.get('/admin/settings'),
    updateSettings: (settings) => api.put('/admin/settings', { settings }),
    testTwilio: (to) => api.post('/admin/integrations/twilio/test', { to }),
};

// Referral API
export const referralsAPI = {
    me: () => api.get('/referrals/me'),
    info: (code) => api.get(`/referrals/info?code=${encodeURIComponent(code)}`),
};

export default api;
